/**
 * Singleton clock and schedule wall-clock helpers.
 *
 * Production code uses real time; tests override via {@link set}.
 */

export interface TimeParts {
	h: number;
	m: number;
}

interface Wall {
	y: number;
	mo: number;
	d: number;
	h: number;
	mi: number;
	wd: number;
}

function gregorianPrevDay(y: number, mo: number, d: number): { y: number; mo: number; d: number } {
	const dt = new Date(Date.UTC(y, mo - 1, d));
	dt.setUTCDate(dt.getUTCDate() - 1);
	return { y: dt.getUTCFullYear(), mo: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
}

function gregorianNextDay(y: number, mo: number, d: number): { y: number; mo: number; d: number } {
	const dt = new Date(Date.UTC(y, mo - 1, d));
	dt.setUTCDate(dt.getUTCDate() + 1);
	return { y: dt.getUTCFullYear(), mo: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
}

function zonedWallFromUtc(utcMs: number, tz: string): Wall {
	const formatter = new Intl.DateTimeFormat('en-US', {
		timeZone: tz,
		hour12: false,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		weekday: 'short',
	} as Intl.DateTimeFormatOptions);

	const parts: Record<string, string> = {};
	formatter.formatToParts(new Date(utcMs)).forEach((part) => {
		if (part.type !== 'literal') {
			parts[part.type] = part.value;
		}
	});

	const weekdayMap: Record<string, number> = {
		Sun: 0,
		Mon: 1,
		Tue: 2,
		Wed: 3,
		Thu: 4,
		Fri: 5,
		Sat: 6,
	};

	let hour = Number(parts.hour);
	if (hour === 24) {
		hour = 0;
	}

	return {
		y: Number(parts.year),
		mo: Number(parts.month),
		d: Number(parts.day),
		h: hour,
		mi: Number(parts.minute),
		wd: weekdayMap[parts.weekday] ?? 0,
	};
}

function wallEquals(w: Wall, y: number, mo: number, d: number, h: number, mi: number): boolean {
	return w.y === y && w.mo === mo && w.d === d && w.h === h && w.mi === mi;
}

function cmpWallToTarget(w: Wall, y: number, mo: number, d: number, h: number, mi: number): number {
	if (w.y !== y) return w.y - y;
	if (w.mo !== mo) return w.mo - mo;
	if (w.d !== d) return w.d - d;
	if (w.h !== h) return w.h - h;
	return w.mi - mi;
}

function findUtcMsForWall(y: number, mo: number, d: number, h: number, mi: number, tz: string): number | null {
	let lo = Date.UTC(y, mo - 1, d) - 40 * 3600000;
	let hi = Date.UTC(y, mo - 1, d) + 40 * 3600000;

	for (let iter = 0; iter < 52; iter++) {
		const mid = (lo + hi) / 2;
		const cmp = cmpWallToTarget(zonedWallFromUtc(mid, tz), y, mo, d, h, mi);
		if (cmp < 0) {
			lo = mid;
		} else {
			hi = mid;
		}
	}

	const wall = zonedWallFromUtc(hi, tz);
	if (!wallEquals(wall, y, mo, d, h, mi)) {
		return null;
	}

	return Math.floor(hi);
}

export class CurrentMoment {
	private static singleton: CurrentMoment | null = null;

	private mockTimestampMs: number | null = null;

	private constructor() {}

	private static instance(): CurrentMoment {
		if (CurrentMoment.singleton === null) {
			CurrentMoment.singleton = new CurrentMoment();
		}
		return CurrentMoment.singleton;
	}

	static nowMs(): number {
		const mock = CurrentMoment.instance().mockTimestampMs;
		return mock !== null ? mock : Date.now();
	}

	static timestamp(): number {
		return Math.floor(CurrentMoment.nowMs() / 1000);
	}

	static set(timestamp: number | null): void {
		CurrentMoment.instance().mockTimestampMs =
			timestamp !== null ? Math.max(0, timestamp) * 1000 : null;
	}

	static reset(): void {
		CurrentMoment.instance().mockTimestampMs = null;
	}

	static parseTime(time: string): TimeParts | null {
		const trimmed = time.trim() || '00:00';
		const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
		if (!match) {
			return null;
		}

		const h = Number(match[1]);
		const m = Number(match[2]);
		if (h < 0 || h > 23 || m < 0 || m > 59) {
			return null;
		}

		return { h, m };
	}

	static clampWeekday(weekday: number): number {
		if (weekday < 0 || weekday > 6) {
			return 0;
		}
		return weekday;
	}

	static lastDailyScheduleAt(time: string, timezone: string | null): Date {
		const parts = CurrentMoment.parseTime(time);
		if (!parts) {
			throw new Error('Invalid daily schedule time.');
		}

		const nowMs = CurrentMoment.nowMs();
		const { h, m } = parts;

		if (timezone) {
			const wall = zonedWallFromUtc(nowMs, timezone);
			let ts = findUtcMsForWall(wall.y, wall.mo, wall.d, h, m, timezone);
			if (ts === null) {
				throw new Error('Unable to resolve daily schedule instant.');
			}
			if (ts <= nowMs) {
				return new Date(ts);
			}
			const prev = gregorianPrevDay(wall.y, wall.mo, wall.d);
			ts = findUtcMsForWall(prev.y, prev.mo, prev.d, h, m, timezone);
			if (ts === null) {
				throw new Error('Unable to resolve daily schedule instant.');
			}
			return new Date(ts);
		}

		const now = new Date(nowMs);
		const todayAt = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0, 0).getTime();
		if (todayAt <= nowMs) {
			return new Date(todayAt);
		}
		const prevDay = gregorianPrevDay(now.getFullYear(), now.getMonth() + 1, now.getDate());
		return new Date(prevDay.y, prevDay.mo - 1, prevDay.d, h, m, 0, 0);
	}

	static lastWeekdayScheduleAt(weekday: number, time: string, timezone: string | null): Date {
		const parts = CurrentMoment.parseTime(time);
		if (!parts) {
			throw new Error('Invalid weekday schedule time.');
		}

		const nowMs = CurrentMoment.nowMs();
		const wdTarget = CurrentMoment.clampWeekday(weekday);
		const { h, m } = parts;
		let best: number | null = null;
		const wall0 = timezone ? zonedWallFromUtc(nowMs, timezone) : null;

		for (let i = 0; i < 21; i++) {
			if (timezone && wall0) {
				let cursor = { y: wall0.y, mo: wall0.mo, d: wall0.d };
				for (let k = 0; k < i; k++) {
					cursor = gregorianPrevDay(cursor.y, cursor.mo, cursor.d);
				}

				const noon = findUtcMsForWall(cursor.y, cursor.mo, cursor.d, 12, 0, timezone);
				if (noon === null || zonedWallFromUtc(noon, timezone).wd !== wdTarget) {
					continue;
				}

				const candidate = findUtcMsForWall(cursor.y, cursor.mo, cursor.d, h, m, timezone);
				if (candidate !== null && candidate <= nowMs && (best === null || candidate > best)) {
					best = candidate;
				}
			} else {
				const day = new Date(nowMs);
				day.setHours(0, 0, 0, 0);
				day.setDate(day.getDate() - i);
				if (day.getDay() !== wdTarget) {
					continue;
				}
				const candidate = new Date(day.getFullYear(), day.getMonth(), day.getDate(), h, m, 0, 0).getTime();
				if (candidate <= nowMs && (best === null || candidate > best)) {
					best = candidate;
				}
			}
		}

		if (best === null) {
			throw new Error('No weekday schedule occurrence found in lookback window.');
		}

		return new Date(best);
	}

	static lastDateScheduleAt(datetime: Date): Date {
		return new Date(datetime.getTime());
	}

	static nextDailyBoundaryMs(time: string, timezone: string | null): number | null {
		const parts = CurrentMoment.parseTime(time);
		if (!parts) {
			return null;
		}

		const nowMs = CurrentMoment.nowMs();
		const { h, m } = parts;

		if (timezone) {
			const wall = zonedWallFromUtc(nowMs, timezone);
			let ts = findUtcMsForWall(wall.y, wall.mo, wall.d, h, m, timezone);
			if (ts !== null && ts > nowMs) {
				return ts;
			}
			const next = gregorianNextDay(wall.y, wall.mo, wall.d);
			return findUtcMsForWall(next.y, next.mo, next.d, h, m, timezone);
		}

		const now = new Date(nowMs);
		const todayAt = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0, 0).getTime();
		if (todayAt > nowMs) {
			return todayAt;
		}
		return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, h, m, 0, 0).getTime();
	}

	static nextWeekdayBoundaryMs(weekday: number, time: string, timezone: string | null): number | null {
		const parts = CurrentMoment.parseTime(time);
		if (!parts) {
			return null;
		}

		const nowMs = CurrentMoment.nowMs();
		const wdTarget = CurrentMoment.clampWeekday(weekday);
		const { h, m } = parts;

		if (timezone) {
			const wall0 = zonedWallFromUtc(nowMs, timezone);
			let cursor = { y: wall0.y, mo: wall0.mo, d: wall0.d };
			for (let n = 0; n < 400; n++) {
				if (n > 0) {
					cursor = gregorianNextDay(cursor.y, cursor.mo, cursor.d);
				}
				const noon = findUtcMsForWall(cursor.y, cursor.mo, cursor.d, 12, 0, timezone);
				if (noon === null) {
					break;
				}
				if (zonedWallFromUtc(noon, timezone).wd !== wdTarget) {
					continue;
				}
				const candidate = findUtcMsForWall(cursor.y, cursor.mo, cursor.d, h, m, timezone);
				if (candidate !== null && candidate > nowMs) {
					return candidate;
				}
			}
			return null;
		}

		for (let n = 0; n < 400; n++) {
			const day = new Date(nowMs);
			day.setHours(0, 0, 0, 0);
			day.setDate(day.getDate() + n);
			if (day.getDay() !== wdTarget) {
				continue;
			}
			const candidate = new Date(day.getFullYear(), day.getMonth(), day.getDate(), h, m, 0, 0).getTime();
			if (candidate > nowMs) {
				return candidate;
			}
		}

		return null;
	}

	static dateScheduleFromParts(date: string, time: string, timezone: string | null): Date | null {
		const match = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
		if (!match) {
			return null;
		}

		const parts = CurrentMoment.parseTime(time);
		if (!parts) {
			return null;
		}

		const y = Number(match[1]);
		const mo = Number(match[2]);
		const d = Number(match[3]);

		if (timezone) {
			const ts = findUtcMsForWall(y, mo, d, parts.h, parts.m, timezone);
			return ts === null ? null : new Date(ts);
		}

		return new Date(y, mo - 1, d, parts.h, parts.m, 0, 0);
	}

	static nextDateBoundaryMs(datetime: Date): number | null {
		const ts = datetime.getTime();
		if (ts <= CurrentMoment.nowMs()) {
			return null;
		}
		return ts;
	}
}
